import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { SyncOutlined, SaveOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { Card, Row, Col, Progress, Typography } from 'antd';
import { FormikPassword, FormikInput, FormikText, FormikButton } from '../../Components/Formik';

import SlsOrdSync01Actions from '../../Stores/SlsOrdSync01/Actions';
import LspDeliveryActions from '../../Stores/LspDeliveryProcess/Actions';
import PurOrdDivisionActions from '../../Stores/PurOrdDivision/Actions';

const { Text } = Typography;

const PurOrdSync01Form = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const syncSetting = useSelector(state => state.lspDeliveryProcess.syncSetting);
  const batchJobStatus = useSelector(state => state.slsOrdSync01.batchJobStatus);
  const isLoading = useSelector(state => state.purOrdDivision.isLoading);
  const curDivisionId = useSelector(state => state.app.curDivisionId);

  useEffect(() => {
    if (curDivisionId > 0) {
      dispatch(LspDeliveryActions.lspDeliveryProcessShowSyncSetting());
    }
  }, [curDivisionId, dispatch]);

  useEffect(() => {
    let timeout;
    if (isLoading) {
      timeout = setTimeout(() => {
        dispatch(SlsOrdSync01Actions.slsOrdSync01ShowBatchJobStatus());
      }, 10000);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading, dispatch]);

  const updateSyncSetting = values => {
    dispatch(PurOrdDivisionActions.purOrdDivisionUpdateSyncSetting(values));
  };

  const syncNow = () => {
    dispatch(PurOrdDivisionActions.purOrdDivisionSyncPurOrd());
  };

  return (
    <Formik
      enableReinitialize
      initialValues={syncSetting}
      onSubmit={values => {
        if (values.submit_action === 'update') {
          updateSyncSetting(values);
        } else if (values.submit_action === 'sync_now') {
          syncNow();
        }
      }}
      validationSchema={Yup.object().shape({
        url: Yup.string().required(intl.formatMessage({ id: 'url_is_required' })),
        username: Yup.string().required(intl.formatMessage({ id: 'username_is_required' })),
        password: Yup.string().required(intl.formatMessage({ id: 'password_is_required' }))
      })}
    >
      {({ handleSubmit }) => (
        <Form>
          <Card>
            {/* URL */}
            <Row type="flex" justify="center" gutter={[8, 8]}>
              <Col span={3}>
                <Text>{intl.formatMessage({ id: 'url' })}</Text>
              </Col>
              <Col span={1}>
                <Text>:</Text>
              </Col>
              <Col span={20}>
                <Field name="url" component={FormikInput} loading={isLoading} />
              </Col>
            </Row>

            {/* Username */}
            <Row type="flex" justify="center" gutter={[8, 8]}>
              <Col span={3}>
                <Text>{intl.formatMessage({ id: 'username' })}</Text>
              </Col>
              <Col span={1}>
                <Text>:</Text>
              </Col>
              <Col span={20}>
                <Field name="username" component={FormikInput} loading={isLoading} />
              </Col>
            </Row>

            {/* Password */}
            <Row type="flex" justify="center" gutter={[8, 8]}>
              <Col span={3}>
                <Text>{intl.formatMessage({ id: 'password' })}</Text>
              </Col>
              <Col span={1}>
                <Text>:</Text>
              </Col>
              <Col span={20}>
                <Field name="password" component={FormikPassword} loading={isLoading} />
              </Col>
            </Row>

            {/* Last Synced */}
            <Row type="flex" justify="center" gutter={[8, 8]}>
              <Col span={3}>
                <Text>{intl.formatMessage({ id: 'last_synced_at' })}</Text>
              </Col>
              <Col span={1}>
                <Text>:</Text>
              </Col>
              <Col span={20}>
                <Field name="last_synced_at" component={FormikText} loading={isLoading} />
              </Col>
            </Row>

            {/* Buttons */}
            <Row type="flex" justify="start" gutter={[0, 16]}>
              <Col span={3}>
                <Field
                  type="primary"
                  name="submit_action"
                  value="update"
                  component={FormikButton}
                  loading={isLoading}
                  onClick={handleSubmit}
                  label={intl.formatMessage({ id: 'update' })}
                  icon={<SaveOutlined />}
                />
              </Col>
              <Col span={3}>
                <Field
                  type="primary"
                  name="submit_action"
                  value="sync_now"
                  component={FormikButton}
                  loading={isLoading}
                  onClick={handleSubmit}
                  label={intl.formatMessage({ id: 'sync_now' })}
                  icon={<SyncOutlined />}
                />
              </Col>
            </Row>

            {/* Progress Bar */}
            <Row type="flex" justify="center" gutter={[0, 8]}>
              <Col span={24}>
                <Progress percent={parseInt(batchJobStatus.status_number, 10)} />
              </Col>
            </Row>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

export default PurOrdSync01Form;
